import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Helmet } from "react-helmet-async";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { 
  MapPin, Search, Phone, ChevronRight, Users, Shield, Clock
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Popular locations to show when no specific location is selected
const POPULAR_LOCATIONS = [
  { name: "Sydney", slug: "sydney", state: "NSW" },
  { name: "Melbourne", slug: "melbourne", state: "VIC" },
  { name: "Brisbane", slug: "brisbane", state: "QLD" },
  { name: "Perth", slug: "perth", state: "WA" },
  { name: "Adelaide", slug: "adelaide", state: "SA" },
  { name: "Gold Coast", slug: "gold-coast", state: "QLD" },
  { name: "Canberra", slug: "canberra", state: "ACT" },
  { name: "Newcastle", slug: "newcastle", state: "NSW" },
  { name: "Sunshine Coast", slug: "sunshine-coast", state: "QLD" },
  { name: "Wollongong", slug: "wollongong", state: "NSW" },
  { name: "Geelong", slug: "geelong", state: "VIC" },
  { name: "Hobart", slug: "hobart", state: "TAS" },
];

export default function ServicePage() {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [searchLocation, setSearchLocation] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchService();
  }, [serviceSlug]);

  const fetchService = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/services`);
      const found = response.data.find(s => s.slug === serviceSlug);
      setService(found);
    } catch (error) {
      console.error("Error fetching service:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchLocation.trim()) {
      const locationSlug = searchLocation.trim().toLowerCase().replace(/\s+/g, '-');
      navigate(`/${serviceSlug}/${locationSlug}`);
    } else {
      toast.error("Please enter a location");
    }
  };

  const serviceName = service?.name || (serviceSlug ? serviceSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Tradies");
  const singular = serviceName.endsWith('s') ? serviceName.slice(0, -1) : serviceName;
  const pageTitle = `${serviceName} Australia | Find Local ${serviceName} | Local Tradies Online`;
  const pageDescription = `Find trusted ${serviceName.toLowerCase()} across Australia. Compare verified local professionals, read reviews & get free quotes. Licensed & insured ${serviceName.toLowerCase()}.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        <Navbar />

        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-24 pb-16">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
            {/* Breadcrumbs */}
            <nav className="flex items-center justify-center text-sm text-slate-400 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <Link to="/browse" className="hover:text-white transition-colors">Find Tradies</Link>
              <ChevronRight className="w-4 h-4 mx-2" />
              <span className="text-white">{serviceName}</span>
            </nav>

            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {serviceName} Australia
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
              Need help finding a {singular.toLowerCase()}? Your search stops here! Enter your location below 
              and we'll connect you with verified, insured {serviceName.toLowerCase()} in your area.
            </p>

            {/* Search Box */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Enter your suburb or city..."
                    value={searchLocation}
                    onChange={(e) => setSearchLocation(e.target.value)}
                    className="pl-12 h-14 text-lg bg-white border-0"
                  />
                </div>
                <Button 
                  type="submit" 
                  size="lg" 
                  className="bg-[#f97316] hover:bg-[#ea580c] h-14 px-8 text-lg"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Search
                </Button>
              </div>
            </form>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <div className="flex items-center text-white">
                <Users className="w-5 h-5 mr-2 text-[#f97316]" />
                <span>1000+ {serviceName}</span>
              </div>
              <div className="flex items-center text-white">
                <Shield className="w-5 h-5 mr-2 text-green-400" />
                <span>Licensed & Insured</span>
              </div>
              <div className="flex items-center text-white">
                <Clock className="w-5 h-5 mr-2 text-blue-400" />
                <span>Free Quotes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Popular Locations */}
        <div className="py-16">
          <div className="container mx-auto px-4 md:px-6 max-w-6xl">
            <h2 
              className="text-2xl md:text-3xl font-bold text-slate-900 mb-8 text-center"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Find {serviceName} By Location
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {POPULAR_LOCATIONS.map((location) => (
                <Link
                  key={location.slug}
                  to={`/${serviceSlug}/${location.slug}`}
                  className="bg-white border border-slate-200 rounded-xl p-4 hover:border-[#f97316] hover:shadow-lg transition-all group"
                >
                  <div className="flex items-center">
                    <MapPin className="w-5 h-5 text-[#f97316] mr-3" />
                    <div>
                      <p className="font-semibold text-slate-900 group-hover:text-[#f97316]">
                        {location.name}
                      </p>
                      <p className="text-sm text-slate-500">{location.state}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Help Section */}
        <div className="bg-white py-16">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl">
            <Card className="border-2 border-[#f97316] bg-orange-50">
              <CardContent className="p-8 text-center">
                <h2 
                  className="text-2xl md:text-3xl font-bold text-slate-900 mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Need Help With Your Search?
                </h2>
                <p className="text-lg text-slate-600 mb-6">
                  Can't find what you're looking for? Let our team help you connect with 
                  the right {singular.toLowerCase()} for your job. Fill out our enquiry form 
                  or give Paul a call directly.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/get-quote">
                    <Button size="lg" className="bg-[#f97316] hover:bg-[#ea580c] text-lg px-8">
                      <Search className="w-5 h-5 mr-2" />
                      Online Enquiry Form
                    </Button>
                  </Link>
                  <a href="tel:0431775771">
                    <Button size="lg" variant="outline" className="border-[#f97316] text-[#f97316] hover:bg-[#f97316] hover:text-white text-lg px-8">
                      <Phone className="w-5 h-5 mr-2" />
                      Call 0431 775 771
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* All Services Link */}
        <div className="py-12 bg-slate-100">
          <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
            <p className="text-slate-600 mb-4">Looking for a different type of tradie?</p>
            <Link to="/browse">
              <Button variant="outline" className="border-slate-400">
                Browse All Trade Categories
              </Button>
            </Link>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
